/**
 * Pure helpers of the glossary page: what it lists, and the two position
 * writes a move makes.
 */

import {
  ChatTag,
  ChatTagCategory,
  ChatTagGlossary,
  PickerOption,
  searchTags,
} from '../tagGlossary';

/** Categories in sheet order, retired ones only when asked. */
export function visibleCategories(
  glossary: ChatTagGlossary,
  showRetired: boolean
): ChatTagCategory[] {
  return glossary.categories.filter((c) => showRetired || !c.archivedAt);
}

/** A category's tags in order, retired ones only when asked. */
export function visibleTags(category: ChatTagCategory, showRetired: boolean): ChatTag[] {
  return category.tags.filter((t) => showRetired || !t.archivedAt);
}

/**
 * Every listed tag of every listed category that matches the query, ranked
 * as the picker ranks (label, category, definition), for the search view.
 */
export function searchGlossary(
  glossary: ChatTagGlossary,
  query: string,
  showRetired: boolean
): PickerOption[] {
  const options: PickerOption[] = [];
  visibleCategories(glossary, showRetired).forEach((category) => {
    visibleTags(category, showRetired).forEach((tag) => options.push({ tag, category }));
  });
  return searchTags(options, query);
}

export interface PositionWrite {
  id: string;
  position: number;
}

/**
 * The two writes that move the row at `index` one step: it takes its
 * neighbour's position and the neighbour takes its own. Null at either end.
 * Two rows on the same position would swap into the same numbers, so the
 * row that ends up below takes the next position and the other keeps its
 * own: no write ever drops under 1, which the dialogs and ant refuse.
 */
export function swapWrites(
  rows: Array<{ id: string; position: number }>,
  index: number,
  direction: 'up' | 'down'
): [PositionWrite, PositionWrite] | null {
  const other = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || other < 0 || index >= rows.length || other >= rows.length) return null;
  const moved = rows[index];
  const neighbour = rows[other];
  if (moved.position === neighbour.position) {
    const next = neighbour.position + 1;
    return direction === 'up'
      ? [
          { id: moved.id, position: moved.position },
          { id: neighbour.id, position: next },
        ]
      : [
          { id: moved.id, position: next },
          { id: neighbour.id, position: neighbour.position },
        ];
  }
  return [
    { id: moved.id, position: neighbour.position },
    { id: neighbour.id, position: moved.position },
  ];
}
